/**
 * ST2086 Metadata Extractor — web version of 2086_extractor_v2.py
 *
 * Extracts Dolby Vision metadata (color space, peak/minimum brightness,
 * MaxCLL/MaxFALL) and generates a populated ST2086 mastering template,
 * mirroring the behavior of the Python tool.
 */

// Configure PDF.js Worker
if (typeof pdfjsLib !== 'undefined') {
  pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
}

(function (global) {
  'use strict';

  // ------------------------------------------------------------------
  // XML helpers (namespace-agnostic, matched by local tag name)
  // ------------------------------------------------------------------

  let parseXmlString = function (text) {
    const doc = new DOMParser().parseFromString(String(text), 'application/xml');
    const errors = doc.getElementsByTagName('parsererror');
    if (errors.length > 0) {
      throw new Error('Invalid XML: ' + ((errors[0].textContent || 'not well-formed').trim().split('\n')[0]));
    }
    return doc.documentElement;
  };

  function localName(el) {
    if (!el) return '';
    if (el.localName) return el.localName;
    return String(el.tagName || el.nodeName || '').replace(/^[^:]*:/, '');
  }

  function childrenOf(el) {
    if (!el || !el.children) return [];
    return Array.prototype.slice.call(el.children);
  }

  /** First descendant (or self) with the given local name, in document order. */
  function findDesc(root, name) {
    if (!root) return null;
    if (localName(root) === name) return root;
    const kids = childrenOf(root);
    for (let i = 0; i < kids.length; i++) {
      const found = findDesc(kids[i], name);
      if (found) return found;
    }
    return null;
  }

  function findChild(parent, name) {
    const kids = childrenOf(parent);
    for (let i = 0; i < kids.length; i++) {
      if (localName(kids[i]) === name) return kids[i];
    }
    return null;
  }

  /** First direct child whose local name contains the given substring. */
  function findChildContaining(parent, sub) {
    const kids = childrenOf(parent);
    for (let i = 0; i < kids.length; i++) {
      if (localName(kids[i]).indexOf(sub) !== -1) return kids[i];
    }
    return null;
  }

  function textOf(el) {
    if (!el) return null;
    const t = (el.textContent != null ? String(el.textContent) : '').trim();
    return t || null;
  }

  // ------------------------------------------------------------------
  // Color space standards and constants (mirroring the Python)
  // ------------------------------------------------------------------

  const P3_PRIMARIES = { Red: [0.680, 0.320], Green: [0.265, 0.690], Blue: [0.150, 0.060] };
  const REC2020_PRIMARIES = { Red: [0.708, 0.292], Green: [0.170, 0.797], Blue: [0.131, 0.046] };
  const BRIGHTNESS_LEVELS = [1000, 2000, 4000];
  const WHITE_POINT = [0.3127, 0.3290];

  const TEMPLATES = (typeof globalThis !== 'undefined' && globalThis.ST2086_TEMPLATES) ? globalThis.ST2086_TEMPLATES : {};

  // ------------------------------------------------------------------
  // Dolby Vision XML parsing
  // ------------------------------------------------------------------

  function parseCoordinates(coordText) {
    const clean = String(coordText === null || coordText === undefined ? '' : coordText).trim();
    if (!clean) return null;
    const parts = clean.indexOf(',') !== -1 ? clean.split(',') : clean.split(/\s+/);
    if (parts.length >= 2) {
      const x = parseFloat(parts[0]);
      const y = parseFloat(parts[1]);
      if (Number.isNaN(x) || Number.isNaN(y)) return null;
      return [x, y];
    }
    return null;
  }

  function matchPrimaries(extracted, standard, tolerance) {
    const colors = ['Red', 'Green', 'Blue'];
    for (let i = 0; i < 3; i++) {
      const p = extracted[colors[i]];
      const s = standard[colors[i]];
      if (!p || !s) return false;
      if (Math.abs(p[0] - s[0]) > tolerance || Math.abs(p[1] - s[1]) > tolerance) return false;
    }
    return true;
  }

  function parseDoviXml(xmlText) {
    const result = {
      colorSpace: null,
      peakBrightness: 1000,
      minBrightness: 0.001,
      maxCll: 0,
      maxFall: 0,
      error: null,
    };

    let root;
    try {
      root = parseXmlString(xmlText);
    } catch (err) {
      result.error = 'Error parsing the XML file. Invalid format.';
      return result;
    }
    if (!root) {
      result.error = 'Error parsing the XML file. Invalid format.';
      return result;
    }

    // Level 6 MaxCLL / MaxFALL
    const level6 = findDesc(root, 'Level6');
    if (level6) {
      const maxCllElem = findChild(level6, 'MaxCLL');
      const maxFallElem = findChild(level6, 'MaxFALL');
      if (maxCllElem && textOf(maxCllElem) !== null) result.maxCll = parseFloat(textOf(maxCllElem));
      if (maxFallElem && textOf(maxFallElem) !== null) result.maxFall = parseFloat(textOf(maxFallElem));
    }

    // MasteringDisplay
    const masteringDisplay = findDesc(root, 'MasteringDisplay');
    if (!masteringDisplay) {
      result.error = "No 'MasteringDisplay' element found in the XML file.";
      return result;
    }

    // Color space from primaries
    const primariesElem = findChildContaining(masteringDisplay, 'Primaries');
    const primaries = {};
    if (primariesElem) {
      const kids = childrenOf(primariesElem);
      for (let i = 0; i < kids.length; i++) {
        const tag = localName(kids[i]);
        if ((tag === 'Red' || tag === 'Green' || tag === 'Blue') && textOf(kids[i]) !== null) {
          const coords = parseCoordinates(textOf(kids[i]));
          if (coords) primaries[tag] = coords;
        }
      }
    }

    if (Object.keys(primaries).length) {
      if (matchPrimaries(primaries, P3_PRIMARIES, 0.01)) {
        result.colorSpace = 'p3';
      } else if (matchPrimaries(primaries, REC2020_PRIMARIES, 0.01)) {
        result.colorSpace = 'rec2020';
      } else {
        result.colorSpace = 'rec2020'; // default fallback (mirrors the Python)
      }
    }

    // Peak brightness / minimum brightness (direct children of MasteringDisplay).
    // The Python tool reads these with ElementTree find('Tag'), which only matches
    // un-namespaced children and therefore falls back to defaults for files with a
    // default XML namespace (Dolby Vision 4.0.2). The web version intentionally reads
    // the real values regardless of namespace.
    const peakBrightnessElem = findChild(masteringDisplay, 'PeakBrightness');
    if (peakBrightnessElem && textOf(peakBrightnessElem) !== null) {
      const brightness = Math.trunc(parseFloat(textOf(peakBrightnessElem)));
      let best = BRIGHTNESS_LEVELS[0];
      for (let i = 1; i < BRIGHTNESS_LEVELS.length; i++) {
        if (Math.abs(BRIGHTNESS_LEVELS[i] - brightness) < Math.abs(best - brightness)) {
          best = BRIGHTNESS_LEVELS[i];
        }
      }
      result.peakBrightness = best;
    }

    // Minimum brightness (direct child of MasteringDisplay)
    const minBrightnessElem = findChild(masteringDisplay, 'MinimumBrightness');
    if (minBrightnessElem && textOf(minBrightnessElem) !== null) {
      result.minBrightness = parseFloat(textOf(minBrightnessElem));
    }

    return result;
  }

  // ------------------------------------------------------------------
  // PDF light-level extraction (mirrors parse_histogram_pdf)
  // ------------------------------------------------------------------

  function extractNumericValue(text, keywords) {
    for (let k = 0; k < keywords.length; k++) {
      const escaped = keywords[k].replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
      const regex = new RegExp(escaped + '[:\\s]+(\\d+(?:\\.\\d+)?)', 'i');
      const match = String(text).match(regex);
      if (match) return parseFloat(match[1]);
    }
    return null;
  }

  async function extractLightLevelsFromPdf(arrayBuffer) {
    const result = { maxCll: null, maxFall: null, error: null };
    if (typeof pdfjsLib === 'undefined') {
      result.error = 'PDF.js library not loaded.';
      return result;
    }
    try {
      const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
      const pdf = await loadingTask.promise;
      let text = '';
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        text += textContent.items.map(function (item) { return item.str; }).join(' ') + '\n';
      }
      result.maxCll = extractNumericValue(text, ['MaxCLL', 'Max CLL', 'MAXCLL']);
      result.maxFall = extractNumericValue(text, ['MaxFALL', 'Max FALL', 'MAXFALL', 'Frame Average']);
    } catch (err) {
      result.error = 'Error processing the PDF: ' + (err && err.message ? err.message : String(err));
    }
    return result;
  }

  // ------------------------------------------------------------------
  // ST2086 template selection and generation (mirrors the Python)
  // ------------------------------------------------------------------

  function getTemplateBasename(colorSpace, peakBrightness, minBrightness) {
    let variantSuffix = '';
    if (minBrightness !== null && minBrightness !== undefined) {
      if (minBrightness >= 0.005) variantSuffix = '-005';
      else if (minBrightness >= 0.0001) variantSuffix = '-0001';
    }
    return '2086_' + colorSpace + '_' + peakBrightness + 'nits' + variantSuffix + '_v9';
  }

  /** Select an embedded ST2086 template, falling back to the base variant (mirrors select_template). */
  function selectTemplate(colorSpace, peakBrightness, minBrightness) {
    if (colorSpace !== 'p3' && colorSpace !== 'rec2020') return null;
    if (BRIGHTNESS_LEVELS.indexOf(peakBrightness) === -1) return null;

    let name = getTemplateBasename(colorSpace, peakBrightness, minBrightness) + '.xml';
    if (TEMPLATES[name]) return { name: name, xml: TEMPLATES[name] };

    const variantRequested = minBrightness !== null && minBrightness !== undefined && minBrightness >= 0.0001;
    if (variantRequested) {
      name = '2086_' + colorSpace + '_' + peakBrightness + 'nits_v9.xml';
      if (TEMPLATES[name]) return { name: name, xml: TEMPLATES[name] };
    }
    return null;
  }

  /** Replace the MaxCLL/MaxFALL values in a template (mirrors populate_template). */
  function setLightLevelValues(xml, maxCll, maxFall) {
    const cllText = String(Math.trunc(maxCll || 0));
    const fallText = String(Math.trunc(maxFall || 0));
    let out = xml.replace(/(<md:ContentMax[^>]*>)([^<]*)(<\/md:ContentMax>)/, '$1' + cllText + '$3');
    out = out.replace(/(<md:FrameAverageMax[^>]*>)([^<]*)(<\/md:FrameAverageMax>)/, '$1' + fallText + '$3');
    return out;
  }

  /** Insert the color-space comment node (mirrors populate_template). */
  function insertColorSpaceComment(xml, colorSpace) {
    const label = colorSpace === 'p3' ? 'P3 MDP' : 'Rec.2020 MDP';
    return xml.replace('<md:MasteredColorVolume>', '<md:MasteredColorVolume><!-- ' + label + ' -->');
  }

  function generateST2086(colorSpace, peakBrightness, minBrightness, maxCll, maxFall) {
    const selected = selectTemplate(colorSpace, peakBrightness, minBrightness);
    if (selected) {
      return insertColorSpaceComment(setLightLevelValues(selected.xml, maxCll, maxFall), colorSpace);
    }

    if (colorSpace !== 'p3' && colorSpace !== 'rec2020') {
      throw new Error('Unsupported color space: ' + colorSpace);
    }

    // Built-in standalone fallback (mirrors build_st2086_template_tree)
    const primaries = colorSpace === 'p3' ? P3_PRIMARIES : REC2020_PRIMARIES;
    const colorSpaceLabel = colorSpace === 'p3' ? 'P3 MDP' : 'Rec.2020 MDP';
    const mb = minBrightness === null || minBrightness === undefined ? 0.001 : minBrightness;
    let minBrightnessStr = mb.toFixed(4);
    if (minBrightnessStr.indexOf('.') !== -1) {
      minBrightnessStr = minBrightnessStr.replace(/0+$/, '').replace(/\.$/, '');
    }

    return '<?xml version="1.0" encoding="UTF-8"?>\n' +
      '<md:Picture xmlns:md="http://www.movielabs.com/md">\n' +
      '\t<md:MasteredColorVolume><!-- ' + colorSpaceLabel + ' -->\n' +
      '\t\t<md:PrimaryRChromaticity>\n' +
      '\t\t\t<md:ChromaticityCIEx>' + primaries.Red[0].toFixed(3) + '</md:ChromaticityCIEx>\n' +
      '\t\t\t<md:ChromaticityCIEy>' + primaries.Red[1].toFixed(3) + '</md:ChromaticityCIEy>\n' +
      '\t\t</md:PrimaryRChromaticity>\n' +
      '\t\t<md:PrimaryGChromaticity>\n' +
      '\t\t\t<md:ChromaticityCIEx>' + primaries.Green[0].toFixed(3) + '</md:ChromaticityCIEx>\n' +
      '\t\t\t<md:ChromaticityCIEy>' + primaries.Green[1].toFixed(3) + '</md:ChromaticityCIEy>\n' +
      '\t\t</md:PrimaryGChromaticity>\n' +
      '\t\t<md:PrimaryBChromaticity>\n' +
      '\t\t\t<md:ChromaticityCIEx>' + primaries.Blue[0].toFixed(3) + '</md:ChromaticityCIEx>\n' +
      '\t\t\t<md:ChromaticityCIEy>' + primaries.Blue[1].toFixed(3) + '</md:ChromaticityCIEy>\n' +
      '\t\t</md:PrimaryBChromaticity>\n' +
      '\t\t<md:WhitePointChromaticity>\n' +
      '\t\t\t<md:ChromaticityCIEx>' + WHITE_POINT[0].toFixed(4) + '</md:ChromaticityCIEx>\n' +
      '\t\t\t<md:ChromaticityCIEy>' + WHITE_POINT[1].toFixed(4) + '</md:ChromaticityCIEy>\n' +
      '\t\t</md:WhitePointChromaticity>\n' +
      '\t\t<md:LuminanceMin>' + minBrightnessStr + '</md:LuminanceMin>\n' +
      '\t\t<md:LuminanceMax>' + peakBrightness.toFixed(2) + '</md:LuminanceMax>\n' +
      '\t</md:MasteredColorVolume>\n' +
      '\t<md:ColorEncoding>\n' +
      '\t\t<md:Primaries>BT2020</md:Primaries>\n' +
      '\t\t<md:TransferFunction>ST2084</md:TransferFunction>\n' +
      '\t\t<md:ColorDifferencing>BT2020</md:ColorDifferencing>\n' +
      '\t</md:ColorEncoding>\n' +
      '\t<md:LightLevel>\n' +
      '\t\t<md:ContentMax interpretation="MaxCLL">' + Math.trunc(maxCll || 0) + '</md:ContentMax>\n' +
      '\t\t<md:FrameAverageMax interpretation="MaxFALL">' + Math.trunc(maxFall || 0) + '</md:FrameAverageMax>\n' +
      '\t</md:LightLevel>\n' +
      '</md:Picture>';
  }

  /** Suggest output filename: {dovi_prefix}{template_stem}.xml (mirrors suggest_output_filename). */
  function suggestOutputFilename(originalName, templateFileName) {
    const name = String(originalName);
    const stem = name.endsWith('.xml') ? name.slice(0, -4) : name;
    const prefix = stem.indexOf('_') !== -1 ? stem.split('_')[0] + '_' : stem;
    const templateStem = String(templateFileName).replace(/\.xml$/, '');
    return prefix + templateStem + '.xml';
  }

  // ------------------------------------------------------------------
  // UI wiring
  // ------------------------------------------------------------------

  function initUi() {
    if (typeof document === 'undefined') return false; //SIP-842

    const form = document.getElementById('extractor-form');
    const doviPath = document.getElementById('dovi-path');
    const doviFile = document.getElementById('dovi-file');
    const doviDropzone = document.getElementById('dovi-dropzone');
    const doviFileInfo = document.getElementById('dovi-file-info');

    const pdfGroup = document.getElementById('pdf-group');
    const pdfPath = document.getElementById('pdf-path');
    const pdfFile = document.getElementById('pdf-file');
    const pdfDropzone = document.getElementById('pdf-dropzone');
    const pdfFileInfo = document.getElementById('pdf-file-info');

    const manualGroup = document.getElementById('manual-group');
    const manualMaxCll = document.getElementById('manual-max-cll');
    const manualMaxFall = document.getElementById('manual-max-fall');

    const submitSpinner = document.getElementById('submit-spinner');
    const btnSubmit = document.getElementById('btn-submit');

    const resultPlaceholder = document.getElementById('result-placeholder');
    const resultContainer = document.getElementById('result-container');

    const metaColorSpace = document.getElementById('meta-color-space');
    const metaPeakBrightness = document.getElementById('meta-peak-brightness');
    const metaMinBrightness = document.getElementById('meta-min-brightness');
    const metaMaxCll = document.getElementById('meta-max-cll');
    const metaMaxFall = document.getElementById('meta-max-fall');

    const statusAlert = document.getElementById('status-alert');
    const xmlOutput = document.getElementById('xml-output');

    const btnCopy = document.getElementById('btn-copy');
    const btnDownload = document.getElementById('btn-download');

    if (!form || !doviPath || !doviFile || !doviDropzone || !doviFileInfo ||
        !pdfGroup || !pdfPath || !pdfFile || !pdfDropzone || !pdfFileInfo ||
        !manualGroup || !manualMaxCll || !manualMaxFall ||
        !submitSpinner || !btnSubmit || !resultPlaceholder || !resultContainer ||
        !metaColorSpace || !metaPeakBrightness || !metaMinBrightness || !metaMaxCll || !metaMaxFall ||
        !statusAlert || !xmlOutput || !btnCopy || !btnDownload) {
      return false; //SIP-842
    }

    let doviXmlContent = null;
    let doviFileName = 'dovi_export';
    let doviMetadata = null;
    let pdfArrayBuffer = null;
    let pdfFileName = '';
    let generatedXml = '';
    let suggestedFileName = 'output.xml';

    function showAlert(message, type) {
      statusAlert.className = 'alert alert-' + type;
      statusAlert.textContent = message;
      statusAlert.classList.remove('hidden');
      if (resultContainer.classList.contains('hidden')) {
        statusAlert.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    function setupDropzone(dropzone, input, fileHandler) {
      dropzone.addEventListener('click', function () { input.click(); });
      input.addEventListener('change', function (e) {
        if (e.target.files.length > 0) fileHandler(e.target.files[0]);
      });
      dropzone.addEventListener('dragover', function (e) { e.preventDefault(); dropzone.classList.add('dragover'); });
      dropzone.addEventListener('dragleave', function () { dropzone.classList.remove('dragover'); });
      dropzone.addEventListener('drop', function (e) {
        e.preventDefault();
        dropzone.classList.remove('dragover');
        if (e.dataTransfer.files.length > 0) {
          input.files = e.dataTransfer.files;
          fileHandler(e.dataTransfer.files[0]);
        }
      });
    }

    setupDropzone(doviDropzone, doviFile, handleDoviFileSelect);
    setupDropzone(pdfDropzone, pdfFile, handlePdfFileSelect);

    // --- Light level source radio ---
    document.querySelectorAll('input[name="light-level-source"]').forEach(function (radio) {
      radio.addEventListener('change', function (e) {
        const value = e.target.value;
        if (value === 'pdf') {
          pdfGroup.classList.remove('hidden');
          manualGroup.classList.add('hidden');
          pdfFile.required = true;
        } else if (value === 'manual') {
          pdfGroup.classList.add('hidden');
          manualGroup.classList.remove('hidden');
          pdfFile.required = false;
        } else {
          pdfGroup.classList.add('hidden');
          manualGroup.classList.add('hidden');
          pdfFile.required = false;
        }
      });
    });

    // Reset caches when the paths are edited
    doviPath.addEventListener('input', function () {
      doviXmlContent = null;
      doviMetadata = null;
      doviFileInfo.textContent = 'No file selected';
      doviFileInfo.classList.remove('file-selected');
    });
    pdfPath.addEventListener('input', function () {
      pdfArrayBuffer = null;
      pdfFileInfo.textContent = 'No file selected';
      pdfFileInfo.classList.remove('file-selected');
    });

      function handleDoviFileSelect(file) {
      doviPath.value = file.name;
      doviFileName = file.name;
      doviFileInfo.textContent = 'Loaded file: ' + file.name;
      doviFileInfo.classList.add('file-selected');
      const reader = new FileReader();
      reader.onload = function (e) {
        doviXmlContent = e.target.result;
        try {
          doviMetadata = parseDoviXml(doviXmlContent);
          if (doviMetadata.error) {
            showAlert(doviMetadata.error, 'error');
          } else {
            showAlert('Dolby Vision XML loaded successfully. Detected color space: ' + (doviMetadata.colorSpace || 'unknown').toUpperCase() + ' at ' + doviMetadata.peakBrightness + ' nits.', 'success');
          }
        } catch (err) {
          showAlert('Error reading XML: ' + (err && err.message ? err.message : String(err)), 'error');
        }
      };
      reader.readAsText(file);
    }

    function handlePdfFileSelect(file) {
      pdfPath.value = file.name;
      pdfFileName = file.name;
      pdfFileInfo.textContent = 'PDF report loaded: ' + file.name;
      pdfFileInfo.classList.add('file-selected');
      const reader = new FileReader();
      reader.onload = function (e) {
        pdfArrayBuffer = e.target.result;
        showAlert('PDF report loaded successfully and ready for analysis.', 'success');
      };
      reader.readAsArrayBuffer(file);
    }

    function renderResults(cll, fall, method) {
      metaColorSpace.textContent = (doviMetadata.colorSpace || '').toUpperCase();
      metaPeakBrightness.textContent = doviMetadata.peakBrightness + ' nits';
      metaMinBrightness.textContent = doviMetadata.minBrightness + ' nits';
      metaMaxCll.textContent = Math.trunc(cll);
      metaMaxFall.textContent = Math.trunc(fall);
      xmlOutput.textContent = generatedXml;
      resultPlaceholder.classList.add('hidden');
      resultContainer.classList.remove('hidden');
      showAlert('Success! ST2086 template generated with MaxCLL: ' + Math.trunc(cll) + ' and MaxFALL: ' + Math.trunc(fall) + ' (' + method + ').', 'success');
      resultContainer.scrollIntoView({ behavior: 'smooth' });
    }

      form.addEventListener('submit', async function (e) {
      e.preventDefault();

      // Required: a Dolby Vision XML file (uploaded or via path). PDF / manual light
      // levels are optional — do not process without an XML.
      if (!doviXmlContent && !doviPath.value.trim()) {
        showAlert('A Dolby Vision XML file is required to generate an ST2086 template. Upload a file or enter its path.', 'error');
        return;
      }

      submitSpinner.classList.remove('hidden');
      btnSubmit.disabled = true;
      try {
        // Load the DoVi XML from the path field if no file was selected
        if (!doviXmlContent) {
          const pathVal = doviPath.value.trim();
          try {
            const response = await fetch(pathVal);
            if (!response.ok) throw new Error('HTTP status ' + response.status);
            doviXmlContent = await response.text();
            doviFileName = pathVal.split('/').pop().split('\\').pop();
            doviMetadata = parseDoviXml(doviXmlContent);
            if (doviMetadata.error) throw new Error(doviMetadata.error);
          } catch (err) {
            throw new Error('Could not read the XML file from the entered path: ' + err.message + '. Make sure the file exists and that the page is being served from a local web server.');
          }
        }

        // Reject early when the loaded XML could not be parsed
        if (doviMetadata.error) {
          throw new Error(doviMetadata.error);
        }

        const sourceVal = document.querySelector('input[name="light-level-source"]:checked').value;
        let finalMaxCll = 0;
        let finalMaxFall = 0;
        let extractionMethod = '';

        if (sourceVal === 'xml') {
          finalMaxCll = doviMetadata.maxCll;
          finalMaxFall = doviMetadata.maxFall;
          extractionMethod = 'extracted from the original XML (Level 6)';
        } else if (sourceVal === 'pdf') {
          if (!pdfArrayBuffer) {
            const pathVal = pdfPath.value.trim();
            if (!pathVal) {
              throw new Error('Please enter a path or select a histogram PDF file.');
            }
            try {
              const response = await fetch(pathVal);
              if (!response.ok) throw new Error('HTTP status ' + response.status);
              pdfArrayBuffer = await response.arrayBuffer();
              pdfFileName = pathVal.split('/').pop().split('\\').pop();
            } catch (err) {
              throw new Error('Could not read the PDF file from the entered path: ' + err.message + '. Make sure the file exists and that the page is being served from a local web server.');
            }
          }
          const pdfData = await extractLightLevelsFromPdf(pdfArrayBuffer);
          if (pdfData.error) throw new Error(pdfData.error);
          finalMaxCll = pdfData.maxCll !== null ? pdfData.maxCll : 0;
          finalMaxFall = pdfData.maxFall !== null ? pdfData.maxFall : 0;
          extractionMethod = 'extracted from the PDF histogram report';
          if (pdfData.maxCll === null || pdfData.maxFall === null) {
            showAlert('Some values could not be extracted from the PDF and were set to 0. Check the file.', 'warning');
          }
        } else {
          finalMaxCll = parseFloat(manualMaxCll.value) || 0;
          finalMaxFall = parseFloat(manualMaxFall.value) || 0;
          extractionMethod = 'entered manually';
        }

        if (doviMetadata.colorSpace === null || doviMetadata.colorSpace === undefined) {
          throw new Error('Unsupported color space: could not determine primaries from the Mastering Display.');
        }

        // Generate ST2086 XML (template-based when available, built-in fallback otherwise)
        generatedXml = generateST2086(
          doviMetadata.colorSpace,
          doviMetadata.peakBrightness,
          doviMetadata.minBrightness,
          finalMaxCll,
          finalMaxFall
        );

        const selected = selectTemplate(
          doviMetadata.colorSpace,
          doviMetadata.peakBrightness,
          doviMetadata.minBrightness
        );
        const templateName = selected
          ? selected.name
          : getTemplateBasename(doviMetadata.colorSpace, doviMetadata.peakBrightness, doviMetadata.minBrightness) + '.xml';
        suggestedFileName = suggestOutputFilename(doviFileName, templateName);

        renderResults(finalMaxCll, finalMaxFall, extractionMethod);
      } catch (err) {
        showAlert(err.message, 'error');
      } finally {
        submitSpinner.classList.add('hidden');
        btnSubmit.disabled = false;
      }
    });

      // --- Copy to clipboard ---
    btnCopy.addEventListener('click', function () {
      if (!generatedXml) return;
      navigator.clipboard.writeText(generatedXml)
        .then(function () {
          const originalText = btnCopy.textContent;
          btnCopy.textContent = 'Copied!';
          btnCopy.style.borderColor = 'var(--ok)';
          btnCopy.style.color = 'var(--ok)';
          setTimeout(function () {
            btnCopy.textContent = originalText;
            btnCopy.style.borderColor = '';
            btnCopy.style.color = '';
          }, 2000);
        })
        .catch(function (err) {
          console.error('Failed to copy XML: ', err);
        });
    });

    // --- Download XML ---
    btnDownload.addEventListener('click', function () {
      if (!generatedXml) return;
      const blob = new Blob([generatedXml], { type: 'application/xml;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', suggestedFileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    });

    return true; //SIP-842
  }

  // ------------------------------------------------------------------
  // Embed boot (hybrid embed: local bundle, no S3)
  // ------------------------------------------------------------------

  //SIP-842
  function resolveEmbedRoot() {
    return (
      document.querySelector('[data-x2086-strapi]') ||
      document.querySelector('[data-nsdr-strapi], [data-nsdr-js]') ||
      document.body
    );
  }

  //SIP-842
  function isSafeToBoot() {
    try {
      const path = String(global.location ? global.location.pathname : '').toLowerCase();
      if (path.indexOf('/admin') >= 0 || path.indexOf('content-manager') >= 0) return false;
      const embed = document.querySelector('[data-x2086-strapi], [data-nsdr-strapi], [data-nsdr-js]');
      if (embed instanceof HTMLElement && embed.closest('[contenteditable="true"], .ck-editor, .ck-editor__editable, .ck-content')) return false;
    } catch (err) {
      return false;
    }
    return true;
  }

  //SIP-842
  function isLocalPreviewPage() {
    return Boolean(
      document.querySelector('#extractor-form') &&
      !document.querySelector('[data-x2086-strapi], [data-nsdr-strapi], [data-nsdr-js]')
    );
  }

  //SIP-842
  function isRootBooted(root) {
    return root instanceof HTMLElement && global.__X2086_BOOTED_ROOTS__ && global.__X2086_BOOTED_ROOTS__.has(root);
  }

  //SIP-842
  function autoInitEmbeddedUi() {
    if (!isSafeToBoot()) return;
    const root = resolveEmbedRoot();
    if (!(root instanceof HTMLElement)) return;
    if (isRootBooted(root) || global.__X2086_BOOTING__) return;
    global.__X2086_BOOTING__ = true;
    if (initUi()) {
      if (!global.__X2086_BOOTED_ROOTS__) global.__X2086_BOOTED_ROOTS__ = new WeakSet();
      global.__X2086_BOOTED_ROOTS__.add(root);
      global.__X2086_BOOTING__ = false;
    } else {
      global.__X2086_BOOTING__ = false;
    }
  }

  // ------------------------------------------------------------------
  // Exports
  // ------------------------------------------------------------------

  const api = {
    parseDoviXml: parseDoviXml,
    parseCoordinates: parseCoordinates,
    matchPrimaries: matchPrimaries,
    extractNumericValue: extractNumericValue,
    extractLightLevelsFromPdf: extractLightLevelsFromPdf,
    getTemplateBasename: getTemplateBasename,
    selectTemplate: selectTemplate,
    generateST2086: generateST2086,
    suggestOutputFilename: suggestOutputFilename,
    P3_PRIMARIES: P3_PRIMARIES,
    REC2020_PRIMARIES: REC2020_PRIMARIES,
    BRIGHTNESS_LEVELS: BRIGHTNESS_LEVELS,
    WHITE_POINT: WHITE_POINT,
    _setXmlParser: function (fn) { parseXmlString = fn; },
    autoInitEmbeddedUi: autoInitEmbeddedUi, //SIP-842
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (global) global.ST2086Extractor = api;

  //SIP-842: local preview only; embeds boot via autoInitEmbeddedUi()
  if (typeof document !== 'undefined' && isLocalPreviewPage()) {
    const runLocalBoot = function () { autoInitEmbeddedUi(); };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', runLocalBoot);
    else runLocalBoot();
  }
})(typeof window !== 'undefined' ? window : globalThis);








